import * as Sentry from '@sentry/react';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import ErrorBoundary from '~/components/ErrorBoundary/ErrorBoundary';

import { Action } from '../../types/Action';
import { Box } from '../Box/Box';
import { IconButton } from '../IconButton/IconButton';
import { Layout } from '../Layout/Layout';
import { PANEL_RIGHT_OFFSET, RIGHT_SPACE } from './constants';
import { PageSidebarContext } from './PageSidebarContext';

export interface Panel_DEPRECATED<V> {
  action: Action<V>;
  component: React.ReactNode;
  /** Default width for this panel's slot. Each panel component sets this in the panels config. The panel can override at runtime via onWidthChange (e.g. maximize). */
  width?: 's' | 'm' | 'fill' | (string | number);
}

interface Props<V extends string> {
  /** Items that will be displayed in sidebar. Use within AppLayout's sidebar slot for correct layout. */
  panels: Record<V, Panel_DEPRECATED<V>>;
  /** is the panel expanded. First icon click opens, second click closes. */
  isExpanded?: boolean;
  /** Sets the selected panel */
  selectedPanel?: V;
  /** The distance from the top:
   *  's' (64px) - just the navbar
   * 'm'(112px) - navbar + subnavbar
   * 'l' (128px) - 2 navbar heights (subnavbar in document viewer is same height as main navbar) */
  topDistance?: 's' | 'm' | 'l';
  /** optional function invoked when sidebar closes */
  onClose?: () => void;
  /** callback called whenever a panel is selected */
  onUpdate?: (panel: V) => void;
  /**
   * Override width for the slot. Use for overlay panels (e.g. Ask AI) that get
   * their width from external state rather than from the panel component.
   * When not provided, width comes from each panel's config (panel.width).
   */
  width?: string | number;
}

const NAVBAR_HEIGHT = 64;
const SUBNAVBAR_HEIGHT = 48;

export const PageSidebar = memo(
  ({
    isExpanded,
    selectedPanel: overridingPanel,
    panels,
    topDistance = 's',
    width: widthOverride,
    onClose,
    onUpdate,
  }: Props<string>) => {
    const [selectedPanel, setSelectedPanel] = useState(
      overridingPanel || Object.keys(panels)[0],
    );
    const [effectiveWidth, setEffectiveWidth] = useState<
      string | number | undefined
    >(undefined);
    const panelConfigWidth = panels[selectedPanel]?.width;
    const fallbackWidth = isWidthToken(panelConfigWidth)
      ? getWidth(panelConfigWidth as string)
      : (panelConfigWidth ?? getWidth('s'));
    const resolvedWidth = widthOverride ?? effectiveWidth ?? fallbackWidth;
    const handleClose = () => {
      onClose?.();
    };

    const setEffectiveWidthCallback = useCallback(
      (w: string | number | undefined) => {
        setEffectiveWidth(w);
      },
      [],
    );

    useEffect(() => {
      if (overridingPanel && overridingPanel !== selectedPanel) {
        setSelectedPanel(overridingPanel);
      }
    }, [overridingPanel]);

    const topPositionStyles = useMemo(() => {
      let top = NAVBAR_HEIGHT;
      if (topDistance === 'm') {
        top += SUBNAVBAR_HEIGHT;
      }
      if (topDistance === 'l') {
        top += NAVBAR_HEIGHT;
      }
      return {
        height: `calc(100vh - ${top}px)`,
        top,
      };
    }, [topDistance]);

    return (
      <PageSidebarContext.Provider
        value={{
          setEffectiveWidth: setEffectiveWidthCallback,
          modeOverride: 'floating-sidepanel',
        }}
      >
        <Layout style={componentStyles.container} data-testid="page-sidebar">
          <Box
            data-testid="panel-container-box"
            overflowX="hidden"
            overflowY="auto"
            minW={0}
            h="100%"
            mr={!isExpanded ? 12 : 0}
            w={isExpanded ? getExpandedPanelWidth(resolvedWidth) : 0}
            styles={[
              {
                visibility: isExpanded ? 'visible' : 'hidden',
                ...topPositionStyles,
              },
            ]}
          >
            <Sentry.ErrorBoundary fallback={ErrorBoundary}>
              {panels[selectedPanel].component}
            </Sentry.ErrorBoundary>
          </Box>
          <Layout
            role="toolbar"
            aria-label="Panel actions"
            data-testid="bar-actions"
            align="flex-start"
            bg="white"
            pt={1}
            pb={1}
            pl={1}
            mr={0}
            styles={[componentStyles.contents, topPositionStyles]}
          >
            <Layout preset="buttons" direction="column">
              {Object.entries(panels).map(([panelName, panel]) => {
                const action = panel.action;
                const isSelected = panelName === selectedPanel;
                return action?.icon ? (
                  <IconButton
                    key={`${action.icon}_${action.label}`}
                    disabled={action.disabled}
                    icon={action.icon}
                    status={isExpanded && isSelected ? 'active' : undefined}
                    tooltip={action.tooltip || ''}
                    onClick={() => {
                      if (isExpanded) {
                        if (isSelected) {
                          action.onClick?.(action);
                          handleClose();
                        } else {
                          setSelectedPanel(panelName);
                          onUpdate?.(panelName);
                        }
                      } else {
                        action.onClick?.(action);
                        setSelectedPanel(panelName);
                        onUpdate?.(panelName);
                      }
                    }}
                  />
                ) : null;
              })}
            </Layout>
          </Layout>
        </Layout>
      </PageSidebarContext.Provider>
    );
  },
);

const componentStyles = {
  container: {
    height: '100%',
  },
  contents: {
    boxShadow: 'raised',
    position: 'fixed',
    right: 0,
  },
  icon: {
    padding: 3,
  },
};
const getWidth = (width: string) => {
  switch (width) {
    case 'fill':
      return `calc(100% - ${RIGHT_SPACE})`;
    case 'm':
      return 'sidebar.width.m';
    default:
      return 'sidebar.width.s';
  }
};

const isWidthToken = (width: unknown) => {
  return width === 'fill' || width === 's' || width === 'm';
};

const getExpandedPanelWidth = (resolvedWidth: string | number) => {
  const offset =
    typeof PANEL_RIGHT_OFFSET === 'string'
      ? parseInt(PANEL_RIGHT_OFFSET, 10) || 0
      : PANEL_RIGHT_OFFSET;
  if (typeof resolvedWidth === 'number') {
    return `${resolvedWidth + offset}px`;
  }
  return `calc(${resolvedWidth} + ${PANEL_RIGHT_OFFSET})`;
};
